/**
 * Column builders for the operations-dashboard alarm/repair/maintenance
 * grids — the presentation layer (`renderCell`) over the field metadata in
 * `operationsColumnFields`. `buildOperationsColumns` is the single source
 * both the CSV export and the live EntityGrid columns read: the export
 * uses the full array (headerName/width/valueGetter/valueFormatter), the
 * live grids use `buildOperationsFieldOverrides`, which strips it down to
 * the `renderCell`/`valueGetter`/`valueFormatter` triad the descriptor
 * doesn't already own.
 *
 * Contains:
 * - buildOperationsColumns — full column array (CSV export)
 * - buildOperationsFieldOverrides — EntityGrid fieldOverrides map (derived)
 * - getMaintenanceColumns — the maintenance tab's plain DataGridPro columns
 */

import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import React from 'react';

import { buildOperationsFieldData } from '@sophocles/features/dashboards/columns/operationsColumnFields';
import { formatDateTime, formatDuration } from '@sophocles/utils/formatUtils';

import AlarmChip from './AlarmChip';
import SiteLink from './SiteLink';

const EMPTY_TERMS = {};

function structureTypeLabels(t) {
  return {
    digital: t('operationsDashboard.structureTypeDigital'),
    static: t('operationsDashboard.structureTypeStatic'),
    both: t('operationsDashboard.structureTypeBoth'),
  };
}

function buildRenderCells({ resolved, customerId, tAlarm, t, user }) {
  const structureTypeLabelMap = structureTypeLabels(t);
  const cells = {
    site_name: params => (
      <SiteLink customerId={customerId} siteGroupId={params.row.site_group_id} siteId={params.row.site_id}>
        {params.value}
      </SiteLink>
    ),
    structure_type: params => structureTypeLabelMap[params.value] || '',
    faces: params => {
      const faces = params.value;
      if (!faces?.length) return '';
      return faces.join(', ');
    },
    alarm_name: params => {
      const cat = params.row.category_key;
      const count = params.row.alarm_count || 1;
      return (
        <AlarmChip
          shortName={cat}
          categoryShortName={cat}
          tAlarm={tAlarm}
          count={count}
          severity={params.row.severity}
        />
      );
    },
    timestamp: params => (params.value ? formatDateTime(params.value, user) : ''),
  };

  if (!resolved) {
    cells.status = params => (
      <Typography
        variant="body2"
        sx={{
          fontSize: 'inherit',
          fontWeight: 600,
          color: params.value === 'active' ? 'error.main' : 'success.main',
        }}
      >
        {params.value === 'active' ? t('alarms.status.active') : t('alarms.status.resolved')}
      </Typography>
    );
    cells.duration = params => (
      <Tooltip title={params.row.timestamp ? new Date(params.row.timestamp).toLocaleString() : ''}>
        <span>{formatDuration(params.value)}</span>
      </Tooltip>
    );
  } else {
    // Same chip treatment as the active grid — every row here is resolved.
    cells.status = () => (
      <Typography variant="body2" sx={{ fontSize: 'inherit', fontWeight: 600, color: 'success.main' }}>
        {t('alarms.status.resolved')}
      </Typography>
    );
    cells.duration = params => <span>{formatDuration(params.value)}</span>;
    cells.resolved_at = params => (params.value ? formatDateTime(params.value, user) : '');
  }

  return cells;
}

/**
 * @param {object} opts
 * @param {boolean} opts.resolved - active-alarms shape (false) vs resolved/repair shape (true).
 * @param {number|string} opts.customerId - route customer id, threaded into SiteLink.
 * @param {{siteGroup: string, site: string}} opts.terms - tenant-term labels.
 * @param {Function} opts.tAlarm - alarm-category translator.
 * @param {Function} opts.t - i18next translator.
 * @param {object} opts.user - current user (date/time formatting locale).
 * @returns {object[]} Full column array — headerName/width/renderCell/valueGetter/valueFormatter/exportOnly.
 */
export function buildOperationsColumns({ resolved, customerId, terms, tAlarm, t, user }) {
  const renderCells = buildRenderCells({ resolved, customerId, tAlarm, t, user });
  const fields = buildOperationsFieldData({ resolved, terms, tAlarm, t, user, formatDateTime, formatDuration });
  return fields.map(fieldDef => {
    const renderCell = renderCells[fieldDef.field];
    return renderCell ? { ...fieldDef, renderCell } : fieldDef;
  });
}

/**
 * Derives the EntityGrid `fieldOverrides` map from {@link buildOperationsColumns}:
 * only fields carrying a `renderCell`/`valueGetter`/`valueFormatter` survive
 * (layout keys like `headerName`/`width` are descriptor-owned), and
 * `exportOnly` fields (CSV-only, e.g. `duration_minutes`) are dropped.
 *
 * `cleared_by` (resolved grid only) isn't part of the shared alarm/repair
 * field table above — it's a descriptor-only column the backend adds to the
 * resolved resource — so its override is layered on separately: a
 * device-auto-cleared alarm reports a null clearer, rendered as "System".
 *
 * `faces` gets a client-side `headerName` override for the same reason
 * SiteGroupGrid/SiteGrid override their own relay-load count columns:
 * "Faces" is billboard vocabulary, so the descriptor's static label is
 * replaced with the customer-type relay-load term ("Panels"/"Zones"/"Faces").
 *
 * @param {object} opts
 * @param {boolean} opts.resolved - active-alarms shape (false) vs resolved/repair shape (true).
 * @param {number|string} opts.customerId - route customer id, threaded into SiteLink.
 * @param {Function} opts.tAlarm - alarm-category translator.
 * @param {object} opts.user - current user (date/time formatting locale).
 * @param {Function} opts.t - i18next translator.
 * @param {{singular: string, plural: string}} opts.relayLoadTerm - customer-type relay-load term.
 * @returns {object} Field name → `{ headerName?, renderCell?, valueGetter?, valueFormatter? }`.
 */
export function buildOperationsFieldOverrides({ resolved, customerId, tAlarm, user, t, relayLoadTerm }) {
  const overrides = {};
  const columns = buildOperationsColumns({ resolved, customerId, terms: EMPTY_TERMS, tAlarm, t, user });
  for (const col of columns) {
    if (col.exportOnly) continue;
    const override = {};
    if (col.renderCell) override.renderCell = col.renderCell;
    if (col.valueGetter) override.valueGetter = col.valueGetter;
    if (col.valueFormatter) override.valueFormatter = col.valueFormatter;
    if (Object.keys(override).length > 0) overrides[col.field] = override;
  }
  if (resolved) {
    overrides.cleared_by = {
      valueFormatter: value => value || t('operationsDashboard.clearedBySystem', { defaultValue: 'System' }),
    };
  }
  if (relayLoadTerm) {
    overrides.faces = { ...overrides.faces, headerName: relayLoadTerm.plural };
  }
  return overrides;
}

export function getMaintenanceColumns(customerId, terms, t) {
  const structureTypeLabelMap = structureTypeLabels(t);
  return [
    { field: 'site_group_name', headerName: terms.siteGroup, minWidth: 120, hideable: false },
    {
      field: 'site_name',
      headerName: terms.site,
      flex: 1,
      minWidth: 120,
      hideable: false,
      renderCell: params => (
        <SiteLink customerId={customerId} siteGroupId={params.row.site_group_id} siteId={params.row.site_id}>
          {params.value}
        </SiteLink>
      ),
    },
    {
      field: 'structure_type',
      headerName: t('general.type'),
      width: 75,
      renderCell: params => structureTypeLabelMap[params.value] || '',
    },
    {
      field: 'status',
      headerName: t('general.status'),
      width: 100,
      hideable: false,
      renderCell: params => (
        <Typography
          variant="body2"
          sx={{
            fontSize: 'inherit',
            fontWeight: 600,
            color: params.value === 'resolved' ? 'success.main' : 'error.main',
          }}
        >
          {params.value === 'resolved' ? t('maintenance.closed') : t('maintenance.open')}
        </Typography>
      ),
    },
    { field: 'date_reported', headerName: t('maintenance.opened'), width: 110 },
    { field: 'entered_by', headerName: t('maintenance.openedBy'), width: 130 },
    {
      field: 'issue',
      headerName: t('sites.issue'),
      flex: 2,
      minWidth: 160,
      hideable: false,
      renderCell: params => (
        <Typography variant="body2" noWrap title={params.value} sx={{ fontSize: 'inherit' }}>
          {params.value}
        </Typography>
      ),
    },
    { field: 'date_resolved', headerName: t('maintenance.closed'), width: 110 },
    { field: 'resolved_by', headerName: t('maintenance.closedBy'), width: 130 },
    {
      field: 'resolution',
      headerName: t('sites.resolution'),
      flex: 1,
      minWidth: 120,
      renderCell: params => (
        <Typography variant="body2" noWrap title={params.value} sx={{ fontSize: 'inherit' }}>
          {params.value}
        </Typography>
      ),
    },
  ];
}
